import type { StepRecord, StepStatus } from '../store/slices/chatSlice';

// The backend persists the SSE step stream as raw events
// (`{type: "step.start" | "step.update" | "step.end", id, ...}`) into
// `chat_messages.message_metadata.steps`. The frontend's canonical
// shape is one merged `StepRecord` per step id (see chatSlice's
// `startMessageStep` / `endMessageStep` reducers — they do exactly
// this merge for the LIVE stream path). On reload via
// `fetchSessionMessages`, we get the raw events back and have to
// reconstruct the merged shape ourselves.
//
// Without this, every historical step renders TWICE (once for the
// start event, once for the end event), and the end events — which
// have no `kind` field — fall through `stepToPart` into the
// `tool-call` branch and show up as bogus "Step" tool-calls.
type RawStepEvent = Partial<StepRecord> & {
  type?: 'step.start' | 'step.update' | 'step.end' | string;
  id?: string;
  kind?: string;
  label?: string;
  detail?: string | null;
  status?: string;
  started_at?: string;
  ended_at?: string | null;
  duration_ms?: number | null;
  result_preview?: string | null;
  error_message?: string | null;
};

function isRawEvent(s: unknown): s is RawStepEvent {
  if (!s || typeof s !== 'object') return false;
  const t = (s as { type?: unknown }).type;
  return t === 'step.start' || t === 'step.update' || t === 'step.end';
}

export function mergePersistedSteps(
  raw: ReadonlyArray<RawStepEvent | StepRecord> | undefined | null,
): StepRecord[] {
  if (!raw || raw.length === 0) return [];

  // If nothing looks like a raw event, the array is already in the
  // canonical merged shape (e.g. live-streamed message just persisted
  // in-memory) — pass through unchanged.
  if (!raw.some(isRawEvent)) return raw as StepRecord[];

  const byId = new Map<string, StepRecord>();
  const order: string[] = [];

  for (const ev of raw) {
    if (!ev || !ev.id) continue;

    if (!isRawEvent(ev)) {
      // Pre-merged record mixed into a raw stream — keep as-is.
      if (!byId.has(ev.id)) {
        order.push(ev.id);
        byId.set(ev.id, ev as StepRecord);
      }
      continue;
    }

    if (ev.type === 'step.start') {
      if (!byId.has(ev.id)) order.push(ev.id);
      byId.set(ev.id, {
        id: ev.id,
        kind: (ev.kind as StepRecord['kind']) ?? 'tool_call',
        label: ev.label ?? '',
        detail: ev.detail ?? null,
        status: 'running',
        started_at: ev.started_at ?? new Date(0).toISOString(),
        ended_at: null,
        duration_ms: null,
        result_preview: null,
        error_message: null,
      });
    } else if (ev.type === 'step.update') {
      const existing = byId.get(ev.id);
      if (!existing) continue;
      if (ev.detail) {
        existing.detail = (existing.detail ?? '') + ev.detail;
      }
    } else if (ev.type === 'step.end') {
      const existing = byId.get(ev.id);
      if (!existing) continue;
      existing.status = (ev.status as StepStatus) === 'error' ? 'error' : 'ok';
      existing.ended_at = ev.ended_at ?? existing.ended_at ?? null;
      existing.duration_ms = ev.duration_ms ?? existing.duration_ms ?? null;
      existing.result_preview =
        ev.result_preview ?? existing.result_preview ?? null;
      existing.error_message =
        ev.error_message ?? existing.error_message ?? null;
    }
  }

  return order.map((id) => byId.get(id)!).filter(Boolean);
}
